import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Copy, CalendarDays, RotateCcw, HelpCircle, UserX } from 'lucide-react';
import { api } from '../../services/api';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtDate } from './format';
import { card, tbl } from '../../theme';

/**
 * ReviewFlags — signals worth a human look within the current filter.
 *
 * Framed the same way the Time and Wage Audit is, and for the same reason:
 * these are discrepancies surfaced for review, NOT adjudicated findings. The
 * importer never deletes or merges anything on the strength of them.
 */
export default function ReviewFlags({ filterParams }) {
  const [threshold, setThreshold] = useState(1000);

  const { data, isLoading } = useQuery({
    queryKey: ['expense-flags', filterParams, threshold],
    queryFn:  () => api.get('/expenses/flags', { params: { ...filterParams, large_threshold: threshold } }),
    keepPreviousData: true,
  });

  if (isLoading && !data) return <LoadingSpinner />;

  const dupes    = data?.possible_duplicate_submissions || [];
  const large    = data?.large_transactions || [];
  const weekend  = data?.weekend_transactions || [];
  const credits  = data?.credits || [];
  const unmapped = data?.unmapped_items || [];
  const flaggedPeople = data?.flagged_status_employees || [];

  return (
    <div style={s.wrap}>
      <div style={s.notice}>
        <HelpCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <strong>These are signals for review, not findings.</strong> Every item below is a pattern
          worth a human look — none of it is evidence of anything on its own, and nothing here has been
          removed from or changed in the underlying data.
        </div>
      </div>

      <Section
        icon={<Copy size={14} />}
        title="Possible duplicate submissions"
        count={dupes.length}
        blurb="Same employee, same amount, same expense item, within 3 days — reimbursements only.
               Company-card repeats are excluded: each carries its own cleared authorisation and is
               almost always a genuine separate purchase."
      >
        {dupes.length === 0 ? <Empty /> : (
          <Table head={['Employee', 'Expense Item', 'Amount', 'Copies', 'Dates', 'Memo', 'Excess']}>
            {dupes.map((r, i) => (
              <tr key={i} style={s.tr}>
                <td style={s.td}>{r.employee_name}</td>
                <td style={s.td}>{r.expense_item}</td>
                <td style={s.tdNum}>{fmtMoney(r.line_amount)}</td>
                <td style={s.tdNum}><span style={s.badge}>×{r.copies}</span></td>
                <td style={s.td}>{fmtDate(r.first_date)}{r.last_date !== r.first_date ? ` → ${fmtDate(r.last_date)}` : ''}</td>
                <td style={{ ...s.td, ...s.memo }} title={r.memo || ''}>{r.memo || '—'}</td>
                <td style={{ ...s.tdNum, fontWeight: 600 }}>{fmtMoney(r.excess_amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        icon={<AlertTriangle size={14} />}
        title="Large transactions"
        count={large.length}
        blurb={
          <span>
            Single lines at or above{' '}
            <input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 0)}
                   style={s.thresholdInput} />
            {' '}— adjust to suit your policy.
          </span>
        }
      >
        {large.length === 0 ? <Empty /> : (
          <Table head={['Date', 'Employee', 'Cost Center', 'Expense Item', 'Merchant', 'Memo', 'Amount']}>
            {large.map((r) => (
              <tr key={r.id} style={s.tr}>
                <td style={s.td}>{fmtDate(r.line_date)}</td>
                <td style={s.td}>{r.employee_name}</td>
                <td style={s.td}>{r.cost_center_code || '—'}</td>
                <td style={s.td}>{r.expense_item}</td>
                <td style={s.td}>{r.merchant_canonical || 'Reimbursement'}</td>
                <td style={{ ...s.td, ...s.memo }} title={r.memo || ''}>{r.memo || '—'}</td>
                <td style={{ ...s.tdNum, fontWeight: 600 }}>{fmtMoney(r.line_amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        icon={<CalendarDays size={14} />}
        title="Weekend transactions"
        count={weekend.length}
        blurb="Lines dated Saturday or Sunday. Entirely legitimate for many roles — useful as context, not as a verdict."
      >
        {weekend.length === 0 ? <Empty /> : (
          <Table head={['Date', 'Employee', 'Expense Item', 'Merchant', 'Memo', 'Amount']}>
            {weekend.slice(0, 50).map((r) => (
              <tr key={r.id} style={s.tr}>
                <td style={s.td}>{fmtDate(r.line_date)}</td>
                <td style={s.td}>{r.employee_name}</td>
                <td style={s.td}>{r.expense_item}</td>
                <td style={s.td}>{r.merchant_canonical || 'Reimbursement'}</td>
                <td style={{ ...s.td, ...s.memo }} title={r.memo || ''}>{r.memo || '—'}</td>
                <td style={{ ...s.tdNum, fontWeight: 600 }}>{fmtMoney(r.line_amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        icon={<RotateCcw size={14} />}
        title="Credits and refunds"
        count={credits.length}
        blurb="Negative lines. Listed because a duplicated credit overstates spend — the error runs in both directions."
      >
        {credits.length === 0 ? <Empty /> : (
          <Table head={['Date', 'Employee', 'Expense Item', 'Merchant', 'Memo', 'Amount']}>
            {credits.map((r) => (
              <tr key={r.id} style={s.tr}>
                <td style={s.td}>{fmtDate(r.line_date)}</td>
                <td style={s.td}>{r.employee_name}</td>
                <td style={s.td}>{r.expense_item}</td>
                <td style={s.td}>{r.merchant_canonical || 'Reimbursement'}</td>
                <td style={{ ...s.td, ...s.memo }} title={r.memo || ''}>{r.memo || '—'}</td>
                <td style={{ ...s.tdNum, fontWeight: 600, color: '#B45309' }}>{fmtMoney(r.line_amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        icon={<UserX size={14} />}
        title="Employees with a status flag"
        count={flaggedPeople.length}
        blurb="Workday tags the PERSON, not the line — a 'Terminated' marker means they are terminated now,
               which does not mean they were when the expense was incurred. Shown with a last-activity date
               so that call is yours to make."
      >
        {flaggedPeople.length === 0 ? <Empty /> : (
          <Table head={['Employee', 'ID', 'Status', 'Transactions', 'Last Activity', 'Amount']}>
            {flaggedPeople.map((r) => (
              <tr key={`${r.employee_name}-${r.employee_id}`} style={s.tr}>
                <td style={s.td}>{r.employee_name}</td>
                <td style={{ ...s.td, color: '#9CA3AF' }}>{r.employee_id || '—'}</td>
                <td style={s.td}><span style={s.badge}>{r.employee_status}</span></td>
                <td style={s.tdNum}>{fmtNumber(r.n)}</td>
                <td style={s.tdNum}>{fmtDate(r.last_activity)}</td>
                <td style={{ ...s.tdNum, fontWeight: 600 }}>{fmtMoney(r.net_amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section
        icon={<AlertTriangle size={14} />}
        title="Unmapped expense items"
        count={unmapped.length}
        blurb="Expense items with no spend-category mapping. They are counted in every total under
               <Unmapped> rather than dropped — add a mapping in Administration → Expense Item Mapping."
      >
        {unmapped.length === 0
          ? <div style={s.allGood}>Every expense item in this selection maps to a spend category.</div>
          : (
            <Table head={['Expense Item', 'Transactions', 'Amount']}>
              {unmapped.map((r) => (
                <tr key={r.expense_item} style={s.tr}>
                  <td style={s.td}>{r.expense_item}</td>
                  <td style={s.tdNum}>{fmtNumber(r.n)}</td>
                  <td style={{ ...s.tdNum, fontWeight: 600 }}>{fmtMoney(r.net_amount)}</td>
                </tr>
              ))}
            </Table>
          )}
      </Section>
    </div>
  );
}

function Section({ icon, title, count, blurb, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={s.card}>
      <button type="button" style={s.cardHead} onClick={() => setOpen((v) => !v)}>
        <span style={s.cardTitle}>{icon}{title}</span>
        <span style={{ ...s.countBadge, ...(count > 0 ? s.countBadgeOn : {}) }}>{count}</span>
      </button>
      <div style={s.blurb}>{blurb}</div>
      {open && children}
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div style={s.scroll}>
      <table style={s.table}>
        <thead><tr>{head.map((h, i) => (
          <th key={h} style={{ ...s.th, textAlign: i >= head.length - 1 ? 'right' : 'left' }}>{h}</th>
        ))}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const Empty = () => <div style={s.allGood}>Nothing flagged in this selection.</div>;

const s = {
  wrap:   { display: 'flex', flexDirection: 'column', gap: '14px' },
  notice: {
    display: 'flex', gap: '9px', padding: '11px 14px',
    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px',
    fontSize: '12px', color: '#1E40AF', lineHeight: 1.5,
  },
  card:      { ...card.sm, padding: '14px 16px' },
  cardHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  cardTitle: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', fontWeight: 600, color: '#111827' },
  countBadge:{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', background: '#F3F4F6', borderRadius: '10px', padding: '1px 9px' },
  countBadgeOn: { background: '#FEF3C7', color: '#92400E' },
  blurb:  { fontSize: '11px', color: '#9CA3AF', margin: '5px 0 10px', lineHeight: 1.5, maxWidth: '780px' },
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th: { padding: '7px 9px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB',
        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#6B7280', whiteSpace: 'nowrap' },
  tr:    { ...tbl.tr, ...tbl.tr },
  td:    { ...tbl.td, ...tbl.tdSm },
  tdNum: { ...tbl.tdR, ...tbl.tdRSm },
  memo:  { maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6B7280' },
  badge: { background: '#FEF3C7', color: '#92400E', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: 600 },
  allGood: { padding: '14px 4px', fontSize: '12px', color: '#6B7280' },
  thresholdInput: { width: '78px', padding: '2px 6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '11px' },
};
